import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import useStore from '@/utils/store/common';
import { useI18n } from 'vue-i18n-composable';
import { useRouter } from 'vue-router/composables';
import moment from 'moment';
import { colors } from '@/constants/theme/colors';
import { DEVICE_ACTIVE_DURATION_THRESHOLD_IN_SECONDS, DEVICE_DISCONNECTED_DURATION_THRESHOLD_IN_SECONDS } from '@/constants/views/DevicesList';
import TrackingRecords from '@/components/common/TrackingRecords/TrackingRecords.vue';
import { V2Device } from 'ctp-search-device';
import { IDevicesListState } from '@/store/modules/devices/types';


export default /*#__PURE__*/_defineComponent({
  __name: 'DeviceInformations',
  props: {
  device: {
    type: Object,
    default: () => ({} as V2Device)
  },
  devicesListState: {
    type: Object,
    default: () => ({} as IDevicesListState)
  },
  deviceSelected: {
    type: Boolean,
    default: false
  }
},
  emits: ['onDeviceSelect'],
  setup(__props, { emit }) {

const props = __props;





const store = useStore();
const router = useRouter();
const i18n = useI18n();

const now = moment();
const themeColors = colors;
const overlay = ref(false);

function getDevicePerso() {
  if (props.device.devicePersonalisationState && props.device.devicePersonalisationState.inProgress) {
    return props.device.devicePersonalisationState.targetPersonalisation;
  }
  if (props.device.devicePersonalisationState && props.device.devicePersonalisationState.currentPersonalisation) {
    return props.device.devicePersonalisationState!.currentPersonalisation;
  }
  return null;
}

function getCurrentUserIsAbleToWrite(featureName: string) {
  return store.getters['permissions/getCurrentUserIsAbleTo']('write', featureName);
}

function onDeviceSelect(e: Event): void {
  e.stopPropagation();
  emit('onDeviceSelect', props.device.id);
}

function copyToClipboard(e: Event, content: any) {
  e.stopPropagation();
  const dummyNode = document.createElement('textarea');
  document.body.appendChild(dummyNode);
  dummyNode.value = content;
  dummyNode.select();
  document.execCommand('copy');
  document.body.removeChild(dummyNode);
}

async function redirectTo(device: V2Device, path: string) {
  let id = device.shortDeviceId;
  if (device.type === 'vdolink' && path === 'personalisation') {
    id = device.devicePersonalisationState?.currentPersonalisation?.persoTargetID;
  }
  router.push({
    name: path,
    params: {
      deviceId: id || ''
    }
  });
}

function getDeviceTypeLabel(deviceType: string) {
  let label = '';
  switch (deviceType) {
  case 'obu5gen':
    label = 'OBU5+';
    break;
  case 'vdolink':
    label = 'VDO Link';
    break;
  default:
    label = 'unknown';
    break;
  }
  return label;
}

function humanizeDuration(date?: Date): string {
  return moment()
    .subtract(moment.duration(now.diff(date)))
    .locale(i18n.locale.value)
    .fromNow();
}

watch(
  () => i18n.locale.value,
  () => humanizeDuration()
);
const updatedAtHumanized = computed(() => {
  if (props.devicesListState.error.devicesActivity) {
    return `${i18n.t('common.status.na')} (${i18n.t('common.status.unavailable_data')})`;
  }
  return props.device.lastSeenTS ? humanizeDuration(moment.unix(props.device.lastSeenTS).toDate()) : `${i18n.t('device.list.never_connected')}`;
});

const disconnected = computed(() => {
  const lastSeen = props.device.lastSeenTS ? moment.unix(props.device.lastSeenTS).toDate() : false;
  return !lastSeen || (Math.abs(moment.duration(now.diff(lastSeen)).asSeconds()) >= DEVICE_DISCONNECTED_DURATION_THRESHOLD_IN_SECONDS);
});

const isActive = computed(() => {
  const lastSeen = props.device.lastSeenTS ? moment.unix(props.device.lastSeenTS).toDate() : false;
  return lastSeen && (Math.abs(moment.duration(now.diff(lastSeen)).asSeconds()) <= DEVICE_ACTIVE_DURATION_THRESHOLD_IN_SECONDS) ? 'device-online' : 'device-offline';
});

const tollingState = computed(() => {
  if (props.device.deviceTollingState && props.device.deviceTollingState.inProgress) {
    return props.device.deviceTollingState.targetState;
  }
  if (props.device.deviceTollingState && props.device.deviceTollingState.currentState) {
    return props.device.deviceTollingState.currentState;
  }
  return null;
});

const devicePlateNumber = computed(() => {
  const perso = getDevicePerso();
  if (perso) {
    return perso.vehicleInfo && perso.vehicleInfo.vehicleLicencePlateNumber?.licencePlateNumber;
  }
  return undefined;
});

const vehicleIconCss = computed(() => {
  const perso = getDevicePerso();
  if (perso && perso.isDeperso) {
    return props.device.devicePersonalisationState!.inProgress ? 'deperso_pending' : 'deperso_current';
  }
  if (perso && props.device.devicePersonalisationState!.inProgress) {
    return 'pending';
  }
  if (perso && !perso.isDeperso && perso.vehicleInfo) {
    return 'current';
  }
  return 'unknown';
});

const statusTooltip = computed(() => {
  const perso = getDevicePerso();
  if (perso && !perso.isDeperso) {
    return props.device.devicePersonalisationState!.inProgress ? 'device.list.item.personalization_in_progress' : 'device.list.item.device_associated';
  } if (perso) {
    return props.device.devicePersonalisationState!.inProgress ? 'device.list.item.depersonalization_in_progress' : 'device.list.item.depersonalized';
  } return '';
});


return { __sfc: true,props, emit, store, router, i18n, now, themeColors, overlay, getDevicePerso, getCurrentUserIsAbleToWrite, onDeviceSelect, copyToClipboard, redirectTo, getDeviceTypeLabel, humanizeDuration, updatedAtHumanized, disconnected, isActive, tollingState, devicePlateNumber, vehicleIconCss, statusTooltip, TrackingRecords }
}

})